import { useMemo } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import _ from 'lodash'

import { useApiAxios } from '@lib/rest-web'
import { useDebounce } from '@lib/web'
import { DataPackages } from '@piv/lib/api-types'

export type { DataPackages as Types }

export const useDataPackageDetail = (
  dataPackageId: string,
  subAssetId: string
) => {
  const api = useApiAxios()

  return useQuery({
    queryKey: ['data-packages/detail', dataPackageId, subAssetId],
    queryFn: async ({ signal }) => {
      const _dataPackageId = encodeURIComponent(dataPackageId)
      const _subAssetId = encodeURIComponent(subAssetId)

      const result = await api.get<DataPackages.DataPackageDetail>(
        `data-packages/${_dataPackageId}/sub-assets/${_subAssetId}/detail`,
        {
          validateStatus: (status) => status === 200,
          signal,
        }
      )

      return result.data
    },
    placeholderData: keepPreviousData,
  })
}

export const useDataPackages = (
  filters: DataPackages.Filters,
  queryString: string
) => {
  const api = useApiAxios()
  const debouncedQuery = useDebounce(queryString, 500)

  // Sometimes old query params may exist which are no longer possible.
  // We have to filter the selections against what's possible to make sure we submit a valid query
  const filtersQuery = useFilters(filters)
  const validFilters = useMemo(() => {
    if (!filtersQuery.data) {
      return filters
    }

    return _.mapValues(
      filters,
      (value: string[], key: keyof DataPackages.Filters) => {
        return value.filter((appliedFilterKey) => {
          return filtersQuery.data[key].some(
            (possibleFilterItem) => possibleFilterItem.key === appliedFilterKey
          )
        })
      }
    )
  }, [filters, filtersQuery.data])

  return useQuery({
    queryKey: ['data-packages', validFilters, debouncedQuery],
    queryFn: async ({ signal }) => {
      const result = await api.get<DataPackages.GetResponse>('data-packages', {
        validateStatus: (status) => status === 200,
        params: { ...validFilters, q: debouncedQuery },
        signal,
      })

      return result.data
    },
    placeholderData: (previous) => (previous ? previous : { packages: [] }),
  })
}

export const useFilters = (queryParams: DataPackages.GetFiltersQueryParams) => {
  const api = useApiAxios()

  return useQuery({
    queryKey: ['data-packages/filters', queryParams],
    queryFn: async ({ signal }) => {
      const result = await api.get<DataPackages.GetFiltersResponse>(
        'data-packages/filters',
        {
          validateStatus: (status) => status === 200,
          params: queryParams,
          signal,
        }
      )

      return result.data
    },
    placeholderData: (previous) =>
      previous ? previous : { ...DataPackages.GetFiltersDefaults },
  })
}
